import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert, Nav, NavItem, NavLink,
} from "reactstrap";
import classnames from "classnames";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { API_BASE, getAuthHeaders } from "../../../helpers/apiConfig";
import { parseApiDate, formatApiDateTime } from "../../../helpers/apiDate";
import { uploadImageToStorage } from "../../../helpers/chatFirebase";
import { matchesSearch } from "../../../helpers/search";

// Reklam alanları: uygulamada anasayfa (yapay zeka ile aynı kayan alan) ve
// kategori detayı (üst kategoriye verilen reklam alt kategorilerde de görünür).
// Şema: REKLAM_ALANLARI.sql · Uygulama okuması: api/Reklam/Aktif
const BASE_URL = API_BASE;

const HEDEF_TIPLERI = [
  { v: "YOK", l: "Tıklanınca bir şey olmasın" },
  { v: "URL", l: "Web sitesi (link)" },
  { v: "ILAN", l: "İlan (ilan no)" },
  { v: "MAGAZA", l: "Mağaza (satıcı kullanıcı ID)" },
  { v: "KATEGORI", l: "Kategori (kategori ID)" },
  { v: "TELEFON", l: "Telefonla ara" },
];
const HEDEF_ETIKET = Object.fromEntries(HEDEF_TIPLERI.map(h => [h.v, h.l]));

const BASVURU_DURUMLARI = [
  { v: "Yeni", l: "Yeni", c: "danger" },
  { v: "Gorusuluyor", l: "Görüşülüyor", c: "warning" },
  { v: "Onaylandi", l: "Onaylandı", c: "success" },
  { v: "Reddedildi", l: "Reddedildi", c: "secondary" },
];
const DURUM_BILGI = Object.fromEntries(BASVURU_DURUMLARI.map(d => [d.v, d]));

const BOS_REKLAM = {
  ID: null, BASLIK: "", ACIKLAMA: "", GORSEL_URL: "", KONUM: "ANASAYFA",
  KATEGORI_ID: null, ALT_KATEGORILERDE_GOSTER: true, HEDEF_TIP: "YOK",
  HEDEF_DEGER: "", REKLAMVEREN_AD: "", SIRA: 0, BASLANGIC_TARIHI: "",
  BITIS_TARIHI: "", AKTIF: true, BASVURU_ID: null,
};

// <input type="datetime-local"> yerel saat bekler; API UTC tutar.
const utcToLocalInput = (raw) => {
  const d = parseApiDate(raw);
  if (!d) return "";
  const p = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const localInputToUtc = (v) => (v ? new Date(v).toISOString() : null);

const yayinDurumu = (r) => {
  if (!r.AKTIF) return { l: "Pasif", c: "secondary" };
  const now = Date.now();
  const bas = parseApiDate(r.BASLANGIC_TARIHI);
  const bit = parseApiDate(r.BITIS_TARIHI);
  if (bas && bas.getTime() > now) return { l: "Planlandı", c: "info" };
  if (bit && bit.getTime() <= now) return { l: "Süresi doldu", c: "dark" };
  return { l: "Yayında", c: "success" };
};

const ReklamYonetimi = () => {
  document.title = "Reklam Yönetimi | HarmanaGEL";

  const [aktifSekme, setAktifSekme] = useState("reklamlar");
  const [reklamlar, setReklamlar] = useState([]);
  const [basvurular, setBasvurular] = useState([]);
  // Ücretli (cüzdandan 499 ₺) başvurular: { [basvuruId]: { Tutar, IadeEdildi } }
  // — api/Reklam/UcretliDurum, ödeme kaydı cüzdan hareketinden okunur.
  const [ucretli, setUcretli] = useState({});
  const [kategoriler, setKategoriler] = useState([]);
  const [baglantilar, setBaglantilar] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(BOS_REKLAM);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [katArama, setKatArama] = useState("");
  const [katAcik, setKatAcik] = useState(false);

  const [notModal, setNotModal] = useState(null); // { basvuru, not }

  const bildir = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [rRes, bRes, kRes, gRes] = await Promise.all([
        fetch(`${BASE_URL}/REKLAMLAR?$orderby=KONUM asc,SIRA asc,ID desc`, { headers: getAuthHeaders() }),
        // Kartla ödemesi tamamlanmamış başvurular (OdemeBekliyor) listelenmez.
        fetch(`${BASE_URL}/REKLAM_BASVURULARI?$filter=DURUM ne 'OdemeBekliyor'&$orderby=ID desc`, { headers: getAuthHeaders() }),
        fetch(`${BASE_URL}/KATEGORILER?$select=ID,AD&$orderby=AD asc`, { headers: getAuthHeaders() }),
        fetch(`${BASE_URL}/KATEGORI_BAGLANTILARI?$select=UST_KATEGORI_ID,ALT_KATEGORI_ID`, { headers: getAuthHeaders() }),
      ]);
      if (!rRes.ok) throw new Error("REKLAMLAR");
      const [r, b, k, g] = await Promise.all([
        rRes.json(),
        bRes.ok ? bRes.json() : { value: [] },
        kRes.json(),
        gRes.json(),
      ]);
      setReklamlar(r.value || []);
      setBasvurular(b.value || []);
      setKategoriler(k.value || []);
      setBaglantilar(g.value || []);
      try {
        const uRes = await fetch(`${BASE_URL}/api/Reklam/UcretliDurum`, { headers: getAuthHeaders() });
        const u = uRes.ok ? await uRes.json() : [];
        setUcretli(Object.fromEntries((u || []).map(x => [x.BasvuruId, x])));
      } catch { /* API eski sürümdeyse rozet görünmez */ }
    } catch {
      setError("Veriler yüklenemedi. REKLAM_ALANLARI.sql çalıştırıldı ve API güncellendi mi?");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── Kategori yolu ("Ana › Alt › AltAlt") ──────────────────────
  const katMap = useMemo(() => Object.fromEntries(kategoriler.map(k => [k.ID, k])), [kategoriler]);
  const ebeveyn = useMemo(() => {
    const m = {};
    baglantilar.forEach(b => { if (!m[b.ALT_KATEGORI_ID]) m[b.ALT_KATEGORI_ID] = b.UST_KATEGORI_ID; });
    return m;
  }, [baglantilar]);
  const katYolu = useCallback((id) => {
    const yol = [];
    const gorulen = new Set();
    let cur = id ? Number(id) : null;
    while (cur && katMap[cur] && !gorulen.has(cur)) {
      gorulen.add(cur);
      yol.unshift(katMap[cur].AD);
      cur = ebeveyn[cur];
    }
    return yol.join(" › ");
  }, [katMap, ebeveyn]);
  const katSecenekleri = useMemo(() => {
    if (!katArama.trim()) return [];
    return kategoriler
      .map(k => ({ id: k.ID, yol: katYolu(k.ID) }))
      .filter(k => matchesSearch(k, katArama, [k.yol, k.id]))
      .slice(0, 50);
  }, [kategoriler, katArama, katYolu]);

  // ─── Reklam formu ──────────────────────────────────────────────
  const f = (alan, deger) => setForm(p => ({ ...p, [alan]: deger }));

  const yeniReklam = (onDolu = {}) => {
    setForm({ ...BOS_REKLAM, ...onDolu });
    setKatArama("");
    setKatAcik(false);
    setModal(true);
  };

  const duzenle = (r) => {
    setForm({
      ...BOS_REKLAM,
      ...r,
      ACIKLAMA: r.ACIKLAMA || "",
      HEDEF_DEGER: r.HEDEF_DEGER || "",
      REKLAMVEREN_AD: r.REKLAMVEREN_AD || "",
      BASLANGIC_TARIHI: utcToLocalInput(r.BASLANGIC_TARIHI),
      BITIS_TARIHI: utcToLocalInput(r.BITIS_TARIHI),
    });
    setKatArama("");
    setKatAcik(false);
    setModal(true);
  };

  const gorselYukle = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Lütfen bir görsel dosyası seçin."); return; }
    setUploadPct(0);
    try {
      const url = await uploadImageToStorage(file, `reklam_gorselleri/reklam_${Date.now()}.jpg`, setUploadPct);
      f("GORSEL_URL", url);
    } catch {
      setError("Görsel yüklenemedi. Firebase Storage kuralında reklam_gorselleri izni var mı?");
    } finally {
      setUploadPct(null);
    }
  };

  const kaydet = async () => {
    setError(null);
    if (!form.BASLIK.trim()) return setError("Başlık zorunlu.");
    if (!form.GORSEL_URL.trim()) return setError("Görsel zorunlu.");
    if (form.KONUM === "KATEGORI" && !form.KATEGORI_ID) return setError("Kategori reklamı için kategori seçin.");

    const payload = {
      BASLIK: form.BASLIK.trim(),
      ACIKLAMA: form.ACIKLAMA.trim() || null,
      GORSEL_URL: form.GORSEL_URL.trim(),
      KONUM: form.KONUM,
      KATEGORI_ID: form.KONUM === "KATEGORI" ? Number(form.KATEGORI_ID) : null,
      ALT_KATEGORILERDE_GOSTER: !!form.ALT_KATEGORILERDE_GOSTER,
      HEDEF_TIP: form.HEDEF_TIP,
      HEDEF_DEGER: form.HEDEF_TIP === "YOK" ? null : (String(form.HEDEF_DEGER || "").trim() || null),
      REKLAMVEREN_AD: form.REKLAMVEREN_AD.trim() || null,
      SIRA: Number(form.SIRA) || 0,
      BASLANGIC_TARIHI: localInputToUtc(form.BASLANGIC_TARIHI),
      BITIS_TARIHI: localInputToUtc(form.BITIS_TARIHI),
      AKTIF: !!form.AKTIF,
      BASVURU_ID: form.BASVURU_ID || null,
    };

    setIsSaving(true);
    try {
      const res = await fetch(
        form.ID ? `${BASE_URL}/REKLAMLAR(${form.ID})` : `${BASE_URL}/REKLAMLAR`,
        { method: form.ID ? "PATCH" : "POST", headers: getAuthHeaders(), body: JSON.stringify(payload) }
      );
      if (!res.ok) {
        let msg = "Kaydedilemedi.";
        try { const j = await res.json(); msg = j?.message || j?.Message || (typeof j === "string" ? j : msg); } catch (_) {}
        if (res.status === 403) msg = "Bu işlem için yönetici yetkisi gerekiyor.";
        throw new Error(msg);
      }
      // Başvurudan oluşturulduysa başvuruyu "Onaylandı" yap.
      if (!form.ID && form.BASVURU_ID) {
        await fetch(`${BASE_URL}/REKLAM_BASVURULARI(${form.BASVURU_ID})`, {
          method: "PATCH", headers: getAuthHeaders(), body: JSON.stringify({ DURUM: "Onaylandi" }),
        });
      }
      setModal(false);
      bildir(form.ID ? "Reklam güncellendi." : "Reklam eklendi.");
      fetchAll();
    } catch (e) {
      setError(e.message);
    } finally {
      setIsSaving(false);
    }
  };

  const aktiflikDegistir = async (r) => {
    const res = await fetch(`${BASE_URL}/REKLAMLAR(${r.ID})`, {
      method: "PATCH", headers: getAuthHeaders(), body: JSON.stringify({ AKTIF: !r.AKTIF }),
    });
    if (res.ok) { bildir(r.AKTIF ? "Reklam yayından kaldırıldı." : "Reklam yayına alındı."); fetchAll(); }
    else setError("Durum değiştirilemedi.");
  };

  const reklamSil = async (r) => {
    if (!window.confirm(`"${r.BASLIK}" reklamı silinsin mi?`)) return;
    const res = await fetch(`${BASE_URL}/REKLAMLAR(${r.ID})`, { method: "DELETE", headers: getAuthHeaders() });
    if (res.ok) { bildir("Reklam silindi."); fetchAll(); } else setError("Silinemedi.");
  };

  // ─── Başvurular ────────────────────────────────────────────────
  const basvuruGuncelle = async (b, alanlar, mesaj) => {
    const res = await fetch(`${BASE_URL}/REKLAM_BASVURULARI(${b.ID})`, {
      method: "PATCH", headers: getAuthHeaders(), body: JSON.stringify(alanlar),
    });
    if (res.ok) { bildir(mesaj); fetchAll(); } else setError("Başvuru güncellenemedi.");
  };

  const basvuruSil = async (b) => {
    if (!window.confirm(`${b.AD_SOYAD} başvurusu silinsin mi?`)) return;
    const res = await fetch(`${BASE_URL}/REKLAM_BASVURULARI(${b.ID})`, { method: "DELETE", headers: getAuthHeaders() });
    if (res.ok) { bildir("Başvuru silindi."); fetchAll(); } else setError("Silinemedi.");
  };

  const ucretIade = async (b) => {
    const u = ucretli[b.ID];
    if (!window.confirm(`${b.AD_SOYAD} başvurusu reddedilip ${u?.Tutar ?? ""} ₺ cüzdanına iade edilsin mi?`)) return;
    const res = await fetch(`${BASE_URL}/api/Reklam/UcretliIade/${b.ID}`, { method: "POST", headers: getAuthHeaders() });
    if (res.ok) { bildir("Ücret cüzdana iade edildi, başvuru reddedildi."); fetchAll(); }
    else {
      let m = "İade yapılamadı.";
      try { const j = await res.json(); m = j?.message || j?.Message || m; } catch { /* yoksay */ }
      setError(m);
    }
  };

  const reklamaDonustur = (b) => {
    setAktifSekme("reklamlar");
    // Ücretli başvuru: yayın penceresi şimdi + 14 gün ön-dolu gelir (ReklamController.ReklamGunSayisi).
    const ucretliMi = !!ucretli[b.ID] && !ucretli[b.ID].IadeEdildi;
    const simdi = new Date();
    yeniReklam({
      ...(ucretliMi ? {
        BASLANGIC_TARIHI: utcToLocalInput(simdi.toISOString()),
        BITIS_TARIHI: utcToLocalInput(new Date(simdi.getTime() + 14 * 24 * 3600 * 1000).toISOString()),
      } : {}),
      BASLIK: b.FIRMA_ADI || b.AD_SOYAD,
      REKLAMVEREN_AD: b.FIRMA_ADI || b.AD_SOYAD,
      KONUM: b.KONUM || "ANASAYFA",
      KATEGORI_ID: b.KATEGORI_ID || null,
      HEDEF_TIP: "TELEFON",
      HEDEF_DEGER: b.TELEFON || "",
      BASVURU_ID: b.ID,
    });
  };

  const yeniBasvuruSayisi = basvurular.filter(b => b.DURUM === "Yeni").length;

  // ─── Kolonlar ──────────────────────────────────────────────────
  const reklamKolonlari = useMemo(() => [
    {
      header: "Görsel", accessorKey: "GORSEL_URL", enableSorting: false,
      cell: c => <img src={c.getValue()} alt="" style={{ width: 120, height: 48, objectFit: "cover", borderRadius: 6 }} />,
    },
    {
      header: "Reklam", accessorKey: "BASLIK", enableSorting: true,
      cell: c => {
        const r = c.row.original;
        return (
          <div style={{ maxWidth: 240, whiteSpace: "normal" }}>
            <div className="fw-medium">{r.BASLIK}</div>
            {r.REKLAMVEREN_AD && <small className="text-muted">{r.REKLAMVEREN_AD}</small>}
          </div>
        );
      },
    },
    {
      header: "Alan", accessorKey: "KONUM", enableSorting: true,
      cell: c => {
        const r = c.row.original;
        if (r.KONUM === "ANASAYFA") return <Badge color="primary">Anasayfa</Badge>;
        return (
          <div style={{ maxWidth: 220, whiteSpace: "normal" }}>
            <Badge color="info">Kategori</Badge>
            <div className="small mt-1">{katYolu(r.KATEGORI_ID) || `#${r.KATEGORI_ID}`}</div>
            {r.ALT_KATEGORILERDE_GOSTER && <small className="text-muted">+ alt kategoriler</small>}
          </div>
        );
      },
    },
    {
      header: "Tıklayınca", accessorKey: "HEDEF_TIP", enableSorting: false,
      cell: c => {
        const r = c.row.original;
        return (
          <div style={{ maxWidth: 200, whiteSpace: "normal" }} className="small">
            {HEDEF_ETIKET[r.HEDEF_TIP] || r.HEDEF_TIP}
            {r.HEDEF_DEGER && <div className="text-muted text-truncate">{r.HEDEF_DEGER}</div>}
          </div>
        );
      },
    },
    {
      header: "Yayın", accessorKey: "AKTIF", enableSorting: false,
      cell: c => {
        const r = c.row.original;
        const d = yayinDurumu(r);
        return (
          <div className="small">
            <Badge color={d.c}>{d.l}</Badge>
            <div className="text-muted mt-1">
              {r.BASLANGIC_TARIHI ? formatApiDateTime(r.BASLANGIC_TARIHI) : "Hemen"} →{" "}
              {r.BITIS_TARIHI ? formatApiDateTime(r.BITIS_TARIHI) : "Süresiz"}
            </div>
          </div>
        );
      },
    },
    {
      header: "Gösterim / Tık", accessorKey: "GOSTERIM_SAYISI", enableSorting: true,
      cell: c => {
        const r = c.row.original;
        const ctr = r.GOSTERIM_SAYISI ? ((r.TIKLAMA_SAYISI / r.GOSTERIM_SAYISI) * 100).toFixed(1) : "0.0";
        return <span className="small">{r.GOSTERIM_SAYISI} / {r.TIKLAMA_SAYISI} <span className="text-muted">(%{ctr})</span></span>;
      },
    },
    { header: "Sıra", accessorKey: "SIRA", enableSorting: true },
    {
      header: "İşlemler", enableSorting: false,
      cell: c => {
        const r = c.row.original;
        return (
          <div className="d-flex gap-1">
            <Button size="sm" color="primary" outline onClick={() => duzenle(r)} title="Düzenle"><i className="bx bx-edit" /></Button>
            <Button size="sm" color={r.AKTIF ? "warning" : "success"} outline onClick={() => aktiflikDegistir(r)} title={r.AKTIF ? "Yayından kaldır" : "Yayına al"}>
              <i className={`bx ${r.AKTIF ? "bx-pause" : "bx-play"}`} />
            </Button>
            <Button size="sm" color="danger" outline onClick={() => reklamSil(r)} title="Sil"><i className="bx bx-trash" /></Button>
          </div>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [katYolu]);

  const basvuruKolonlari = useMemo(() => [
    {
      header: "Tarih", accessorKey: "OLUSTURMA_TARIHI", enableSorting: true,
      cell: c => <span className="small">{formatApiDateTime(c.getValue())}</span>,
    },
    {
      header: "Başvuran", accessorKey: "AD_SOYAD", enableSorting: true,
      cell: c => {
        const b = c.row.original;
        return (
          <div>
            <div className="fw-medium">{b.AD_SOYAD}</div>
            {b.FIRMA_ADI && <small className="text-muted">{b.FIRMA_ADI}</small>}
            {b.KULLANICI_ID && <div><Badge color="light" className="text-dark border">Üye #{b.KULLANICI_ID}</Badge></div>}
          </div>
        );
      },
    },
    {
      header: "İletişim", accessorKey: "TELEFON", enableSorting: false,
      cell: c => {
        const b = c.row.original;
        return (
          <div className="small">
            <a href={`tel:${b.TELEFON}`}>{b.TELEFON}</a>
            {b.EPOSTA && <div><a href={`mailto:${b.EPOSTA}`}>{b.EPOSTA}</a></div>}
          </div>
        );
      },
    },
    {
      header: "Alan", accessorKey: "KONUM", enableSorting: true,
      cell: c => {
        const b = c.row.original;
        return b.KONUM === "KATEGORI"
          ? <div className="small" style={{ maxWidth: 200, whiteSpace: "normal" }}><Badge color="info">Kategori</Badge> {katYolu(b.KATEGORI_ID)}</div>
          : <Badge color="primary">Anasayfa</Badge>;
      },
    },
    {
      header: "Mesaj", accessorKey: "MESAJ", enableSorting: false,
      cell: c => {
        const b = c.row.original;
        return (
          <div className="small" style={{ maxWidth: 260, whiteSpace: "normal" }}>
            {ucretli[b.ID] && (
              <Badge color={ucretli[b.ID].IadeEdildi ? "secondary" : "success"} className="mb-1 me-1">
                {ucretli[b.ID].IadeEdildi ? "Ücret iade edildi" : `Ücretli — ${ucretli[b.ID].Tutar} ₺ ödendi`}
              </Badge>
            )}
            {b.MESAJ || <span className="text-muted">—</span>}
            {b.ADMIN_NOTU && <div className="text-primary mt-1"><i className="bx bx-note me-1" />{b.ADMIN_NOTU}</div>}
          </div>
        );
      },
    },
    {
      header: "Durum", accessorKey: "DURUM", enableSorting: true,
      cell: c => {
        const b = c.row.original;
        return (
          <Input
            type="select" bsSize="sm" value={b.DURUM}
            className={`border-${DURUM_BILGI[b.DURUM]?.c || "secondary"}`}
            onChange={e => basvuruGuncelle(b, { DURUM: e.target.value }, "Başvuru durumu güncellendi.")}
            style={{ minWidth: 130 }}
          >
            {BASVURU_DURUMLARI.map(d => <option key={d.v} value={d.v}>{d.l}</option>)}
          </Input>
        );
      },
    },
    {
      header: "İşlemler", enableSorting: false,
      cell: c => {
        const b = c.row.original;
        return (
          <div className="d-flex gap-1">
            <Button size="sm" color="success" onClick={() => reklamaDonustur(b)} title="Bu başvurudan reklam oluştur">
              <i className="bx bx-plus-circle me-1" />Reklam
            </Button>
            <Button size="sm" color="secondary" outline onClick={() => setNotModal({ basvuru: b, not: b.ADMIN_NOTU || "" })} title="Not">
              <i className="bx bx-note" />
            </Button>
            {ucretli[b.ID] && !ucretli[b.ID].IadeEdildi && (
              <Button size="sm" color="warning" outline onClick={() => ucretIade(b)} title="Reddet ve ücreti cüzdana iade et">
                <i className="bx bx-undo me-1" />İade
              </Button>
            )}
            {!ucretli[b.ID] && (
              <Button size="sm" color="danger" outline onClick={() => basvuruSil(b)} title="Sil"><i className="bx bx-trash" /></Button>
            )}
          </div>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [katYolu, ucretli]);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="İçerik Yönetimi" breadcrumbItem="Reklam Yönetimi" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <Nav tabs className="nav-tabs-custom">
                  <NavItem>
                    <NavLink className={classnames({ active: aktifSekme === "reklamlar" })} style={{ cursor: "pointer" }} onClick={() => setAktifSekme("reklamlar")}>
                      <i className="bxs-megaphone bx me-1" />Reklamlar <Badge color="light" className="text-dark ms-1">{reklamlar.length}</Badge>
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink className={classnames({ active: aktifSekme === "basvurular" })} style={{ cursor: "pointer" }} onClick={() => setAktifSekme("basvurular")}>
                      <i className="bx bx-news me-1" />Başvurular
                      {yeniBasvuruSayisi > 0 && <Badge color="danger" pill className="ms-1">{yeniBasvuruSayisi} yeni</Badge>}
                    </NavLink>
                  </NavItem>
                </Nav>
                {aktifSekme === "reklamlar" && (
                  <Button color="primary" onClick={() => yeniReklam()}>
                    <i className="bx bx-plus me-1" />Yeni Reklam
                  </Button>
                )}
              </div>

              <p className="text-muted small">
                <strong>Anasayfa</strong> reklamları uygulamada yapay zeka alanıyla aynı kayan şeritte gösterilir.{" "}
                <strong>Kategori</strong> reklamları o kategoride ve (işaretliyse) tüm alt kategorilerinde görünür.
                Bir alanda yayında reklam yoksa kullanıcıya "Reklam için başvur" kartı çıkar.
                Görsel önerisi: <strong>1200×300</strong> (4:1). Kategori alanı daha dar olduğu için
                görselin kenarları biraz kırpılır — yazı ve logoyu <strong>ortada</strong> tutun.
              </p>

              {isLoading ? (
                <div className="text-center py-5"><Spinner color="primary" /></div>
              ) : aktifSekme === "reklamlar" ? (
                <TableContainer
                  columns={reklamKolonlari}
                  data={reklamlar}
                  isGlobalFilter={true}
                  isPagination={true}
                  customPageSize={20}
                  SearchPlaceholder="Reklam, reklamveren veya kategori ara..."
                  tableClass="align-middle table-nowrap table-hover"
                  theadClass="table-light"
                  paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  pagination="pagination pagination-rounded justify-content-end mb-2"
                />
              ) : (
                <TableContainer
                  columns={basvuruKolonlari}
                  data={basvurular}
                  isGlobalFilter={true}
                  isPagination={true}
                  customPageSize={20}
                  SearchPlaceholder="Ad, firma, telefon ara..."
                  tableClass="align-middle table-nowrap table-hover"
                  theadClass="table-light"
                  paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  pagination="pagination pagination-rounded justify-content-end mb-2"
                />
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      {/* ── Reklam ekle/düzenle ── */}
      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="lg">
        <ModalHeader toggle={() => setModal(false)}>
          {form.ID ? `Reklamı Düzenle (#${form.ID})` : "Yeni Reklam"}
          {form.BASVURU_ID && !form.ID && <Badge color="success" className="ms-2">Başvuru #{form.BASVURU_ID}</Badge>}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={8}>
              <FormGroup>
                <Label className="fw-bold">Başlık *</Label>
                <Input value={form.BASLIK} maxLength={200} onChange={e => f("BASLIK", e.target.value)} placeholder="Panelde görünen ad" />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold">Reklamveren</Label>
                <Input value={form.REKLAMVEREN_AD} onChange={e => f("REKLAMVEREN_AD", e.target.value)} />
              </FormGroup>
            </Col>

            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">Görsel *</Label>
                <div className="d-flex gap-2 align-items-center">
                  <Input value={form.GORSEL_URL} onChange={e => f("GORSEL_URL", e.target.value)} placeholder="https://... veya dosya yükleyin" />
                  <Label className="btn btn-outline-primary mb-0 text-nowrap">
                    {uploadPct !== null ? <><Spinner size="sm" className="me-1" />%{uploadPct}</> : <><i className="bx bx-upload me-1" />Yükle</>}
                    <input type="file" accept="image/*" hidden onChange={gorselYukle} disabled={uploadPct !== null} />
                  </Label>
                </div>
                {form.GORSEL_URL && (
                  <img src={form.GORSEL_URL} alt="" className="mt-2 border rounded" style={{ width: "100%", maxWidth: 420, aspectRatio: "4 / 1", objectFit: "cover" }} />
                )}
              </FormGroup>
            </Col>

            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">Reklam alanı *</Label>
                <div className="d-flex gap-3">
                  {["ANASAYFA", "KATEGORI"].map(k => (
                    <FormGroup check key={k}>
                      <Input type="radio" name="konum" id={`konum-${k}`} checked={form.KONUM === k} onChange={() => f("KONUM", k)} />
                      <Label check for={`konum-${k}`}>{k === "ANASAYFA" ? "Anasayfa (yapay zeka şeridi)" : "Kategori detayı"}</Label>
                    </FormGroup>
                  ))}
                </div>
              </FormGroup>
            </Col>

            {form.KONUM === "KATEGORI" && (
              <Col md={12}>
                <FormGroup className="position-relative">
                  <Label className="fw-bold">Kategori *</Label>
                  <Input
                    value={katAcik ? katArama : (form.KATEGORI_ID ? katYolu(form.KATEGORI_ID) : "")}
                    placeholder="Kategori ara (ör. traktör, kurbanlık)…"
                    onFocus={() => { setKatAcik(true); setKatArama(""); }}
                    onChange={e => { setKatArama(e.target.value); setKatAcik(true); }}
                    onBlur={() => setTimeout(() => setKatAcik(false), 150)}
                  />
                  {katAcik && katSecenekleri.length > 0 && (
                    <div className="border rounded bg-white shadow-sm position-absolute w-100" style={{ zIndex: 1000, maxHeight: 240, overflowY: "auto" }}>
                      {katSecenekleri.map(k => (
                        <button
                          type="button" key={k.id}
                          className="d-block w-100 text-start border-0 bg-transparent px-2 py-1 small"
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => { f("KATEGORI_ID", k.id); setKatAcik(false); }}
                        >
                          {k.yol} <span className="text-muted">#{k.id}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <FormGroup check className="mt-2">
                    <Input type="checkbox" id="altkat" checked={!!form.ALT_KATEGORILERDE_GOSTER} onChange={e => f("ALT_KATEGORILERDE_GOSTER", e.target.checked)} />
                    <Label check for="altkat">Alt kategorilerde de göster</Label>
                  </FormGroup>
                </FormGroup>
              </Col>
            )}

            <Col md={5}>
              <FormGroup>
                <Label className="fw-bold">Tıklayınca</Label>
                <Input type="select" value={form.HEDEF_TIP} onChange={e => f("HEDEF_TIP", e.target.value)}>
                  {HEDEF_TIPLERI.map(h => <option key={h.v} value={h.v}>{h.l}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md={7}>
              <FormGroup>
                <Label className="fw-bold">Hedef</Label>
                <Input
                  value={form.HEDEF_DEGER}
                  disabled={form.HEDEF_TIP === "YOK"}
                  onChange={e => f("HEDEF_DEGER", e.target.value)}
                  placeholder={
                    form.HEDEF_TIP === "URL" ? "https://..." :
                    form.HEDEF_TIP === "TELEFON" ? "05xx xxx xx xx" :
                    form.HEDEF_TIP === "YOK" ? "" : "Sayısal ID"
                  }
                />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold">Başlangıç</Label>
                <Input type="datetime-local" value={form.BASLANGIC_TARIHI} onChange={e => f("BASLANGIC_TARIHI", e.target.value)} />
                <small className="text-muted">Boş = hemen</small>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold">Bitiş</Label>
                <Input type="datetime-local" value={form.BITIS_TARIHI} onChange={e => f("BITIS_TARIHI", e.target.value)} />
                <small className="text-muted">Boş = süresiz</small>
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label className="fw-bold">Sıra</Label>
                <Input type="number" value={form.SIRA} onChange={e => f("SIRA", e.target.value)} />
              </FormGroup>
            </Col>
            <Col md={2} className="d-flex align-items-center">
              <FormGroup check className="mt-3">
                <Input type="checkbox" id="aktif" checked={!!form.AKTIF} onChange={e => f("AKTIF", e.target.checked)} />
                <Label check for="aktif">Aktif</Label>
              </FormGroup>
            </Col>

            <Col md={12}>
              <FormGroup className="mb-0">
                <Label className="fw-bold">Açıklama (panel notu)</Label>
                <Input type="textarea" rows={2} value={form.ACIKLAMA} maxLength={500} onChange={e => f("ACIKLAMA", e.target.value)} />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)} disabled={isSaving}>İptal</Button>
          <Button color="primary" onClick={kaydet} disabled={isSaving || uploadPct !== null}>
            {isSaving ? <Spinner size="sm" className="me-1" /> : <i className="mdi mdi-content-save me-1" />}
            {form.ID ? "Güncelle" : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── Başvuru notu ── */}
      <Modal isOpen={!!notModal} toggle={() => setNotModal(null)} centered>
        <ModalHeader toggle={() => setNotModal(null)}>Başvuru Notu</ModalHeader>
        <ModalBody>
          <Input
            type="textarea" rows={4} maxLength={1000}
            value={notModal?.not || ""}
            onChange={e => setNotModal(p => ({ ...p, not: e.target.value }))}
            placeholder="Görüşme notu, teklif edilen fiyat vb."
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setNotModal(null)}>İptal</Button>
          <Button
            color="primary"
            onClick={async () => {
              const { basvuru, not } = notModal;
              setNotModal(null);
              await basvuruGuncelle(basvuru, { ADMIN_NOTU: not.trim() || null }, "Not kaydedildi.");
            }}
          >Kaydet</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default ReklamYonetimi;
